import cn from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Column, Row } from 'react-table';

import { ReactComponent as EditIcon } from '../../assets/images/icon-edit-gray.svg';
import { AddButton, AutomationTable } from '../../components';
import { fetchAutomatedCampaignsRequest } from '../../store/actions/automations';
import { selectAutomatedCampaigns } from '../../store/selectors/automations';
import { AutomatedCampaignTypeEnum, IAutomatedCampaign } from '../../types/automations';
import { getAutomationStatusLabel } from '../../utils/automations';
import { getFullName } from '../../utils/helpers';

import styles from './AutomatedCampaigns.module.scss';

interface IProps {
  disabled: boolean;
  onAdd: () => void;
  onEdit: (campaign: IAutomatedCampaign) => void;
}

const AutomatedCampaignsContainer: React.FC<IProps> = ({ disabled, onAdd, onEdit }) => {
  const dispatch = useDispatch();

  const campaigns = useSelector(selectAutomatedCampaigns);

  const getTypeLabel = (type: AutomatedCampaignTypeEnum): string => {
    switch (type) {
      case AutomatedCampaignTypeEnum.NewHire:
        return 'New Hire';
      case AutomatedCampaignTypeEnum.Birthday:
        return 'Birthday';
      case AutomatedCampaignTypeEnum.Anniversary:
        return 'Anniversary';
      default:
        return '';
    }
  };

  const columns: Column<IAutomatedCampaign>[] = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Type',
        accessor: 'type',
        Cell: ({ value }: { value: AutomatedCampaignTypeEnum }) => <span>{getTypeLabel(value)}</span>,
      },
      {
        Header: 'Sender',
        Cell: ({ row: { original } }: { row: Row<IAutomatedCampaign> }) => (
          <span>{getFullName(original.sender_first_name, original.sender_last_name)}</span>
        ),
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }: { value: string }) => {
          const { message, color } = getAutomationStatusLabel(value, 'Campaign');

          return (
            <div className={styles.statusWrapper}>
              <div
                style={{
                  backgroundColor: color,
                }}
                className={styles.statusBullet}
              />
              {message}
            </div>
          );
        },
      },
      {
        id: 'action',
        Cell: ({ row }: { row: Row<IAutomatedCampaign> }) => (
          <button type="button" onClick={() => onEdit(row.original)}>
            <EditIcon />
          </button>
        ),
      },
    ],
    [onEdit],
  );

  useEffect(() => {
    dispatch(fetchAutomatedCampaignsRequest());
  }, [dispatch]);

  return (
    <div
      className={cn({
        [styles.disabled]: disabled,
      })}
    >
      <div className={styles.header}>
        <AddButton assetName="campaign" onClick={onAdd} className={styles.addButton} />
      </div>
      <AutomationTable
        data={campaigns || []}
        columns={columns}
        placeholderText="There are no campaigns"
        containerClassName={styles.tableContainer}
      />
    </div>
  );
};

export default AutomatedCampaignsContainer;
